/* eslint-disable no-irregular-whitespace */
import React from "react";
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
} from "react-hook-form";
import UploadFiles, {
  FileType,
} from "../../../../shared/components/UploadFiles";
import Dropzone from "../../../shared/components/app/Dropzone";
import { Box } from "../../../shared/components/ui/chakra";
import Input from "../../../shared/components/ui/Input";
import InputError from "../../../shared/components/ui/InputError";
import Radio from "../../../shared/components/ui/Radio";
import Textarea from "../../../shared/components/ui/Textarea";
import Select from "../../../shared/components/ui/Select";
import Checkbox from "../../../shared/components/ui/Checkbox";
import { Options } from "../../../shared/lib/types";
import getEstablishmentYears from "../lib/getEstablishmentYears";
import { useCaseOptions } from "../../../../shared/lib/useCasesOptions";
import { Step1FormData } from "../lib/useStep1Form";
import { Step2FormData } from "../lib/useStep2Form";

import { HStack, InputProps, RadioGroup, Stack, Text } from "@chakra-ui/react";

type Step1Field<TFieldName extends FieldPath<Step1FormData>> =
  ControllerRenderProps<
    Step1FormData | (Step1FormData & Step2FormData),
    TFieldName
  >;

type Step2Field<TFieldName extends FieldPath<Step2FormData>> =
  ControllerRenderProps<
    Step2FormData | (Step1FormData & Step2FormData),
    TFieldName
  >;

export const NameField = ({
  field,
  fieldState: { error },
  onInput,
  isNoLabel = false,
}: {
  field: Step1Field<"name">;
  fieldState: ControllerFieldState;
  onInput: () => void;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      id="name"
      {...field}
      required
      label={isNoLabel ? undefined : "事業者名"}
      placeholder="例）エーループ設計事務所"
      error={error?.message}
      onInput={onInput}
    />
  );
};

export const NameKanaField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"name_kana">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      id="name_kana"
      {...field}
      label={isNoLabel ? undefined : "事業者名かな"}
      placeholder="例）えーるーぷせっけいじむしょ"
      error={error?.message}
    />
  );
};

export const BranchNameField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"branch_name">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      id="branch_name"
      {...field}
      label={isNoLabel ? undefined : "支店名"}
      placeholder="例）大阪支店"
      message="本社の場合、入力する必要はありません。"
      error={error?.message}
    />
  );
};

export const PostalCodeField = ({
  field,
  fieldState: { error },
  onChange,
  isNoLabel = false,
}: {
  field: Step1Field<"postal_code">;
  fieldState: ControllerFieldState;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      required
      label={isNoLabel ? undefined : "郵便番号"}
      placeholder="例）1234567"
      error={error?.message}
      onChange={onChange}
    />
  );
};

export const PrefectureField = ({
  field,
  fieldState: { error },
  prefectures,
  isNoLabel = false,
}: {
  field: Step1Field<"prefecture_id">;
  fieldState: ControllerFieldState;
  prefectures: { id: string | number; name: string }[];
  isNoLabel?: boolean;
}) => {
  return (
    <Select
      required
      label={isNoLabel ? undefined : "都道府県"}
      error={error?.message ?? ""}
      {...field}
    >
      <option value="" key="empty">
        選択してください
      </option>
      {prefectures.map((prefecture) => (
        <option value={prefecture.id} key={prefecture.id}>
          {prefecture.name}
        </option>
      ))}
    </Select>
  );
};

export const Address1Field = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"address1">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      required
      label={isNoLabel ? undefined : "市区町村"}
      placeholder="市区町村を入力してください"
      error={error?.message}
    />
  );
};

export const Address2Field = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"address2">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      required
      label={isNoLabel ? undefined : "番地"}
      placeholder="例）1-23-45"
      error={error?.message}
    />
  );
};

export const Address3Field = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"address3">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "建物名"}
      placeholder="建物名を入力してください"
      error={error?.message}
    />
  );
};

export const ShowUpToAddress1Field = ({
  field,
}: {
  field: Step1Field<"display_address_restricted">;
}) => {
  return (
    <Stack px={5} py={4} bgColor="brand.50" gap={2}>
      <Checkbox
        isChecked={field.value}
        onChange={field.onChange}
        ref={field.ref}
        isDisabled={field.disabled}
      >
        所在地の一部を非表示にする
      </Checkbox>
      <Box fontSize={{ base: "xs", md: "sm" }}>
        事業者所在地の一部を非表示にしたい場合はチェックを入れてください。
        <br />
        チェックを入れると事業者情報に市区町村までが表示されます。（例：東京都新宿区）
      </Box>
    </Stack>
  );
};

export const PhoneNumberField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"phone_number">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      required
      label={isNoLabel ? undefined : "電話番号"}
      error={error?.message}
      placeholder="例）0123456789"
    />
  );
};

export const EmailField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"email">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      required
      label={isNoLabel ? undefined : "受発注の通知を受け取るメールアドレス"}
      error={error?.message}
      placeholder="例）example@aloop.jp"
      message="会員登録時と異なるメールアドレスも指定できます。その場合は、メール認証が必要です。"
    />
  );
};

export const IndustryField = ({
  field,
  fieldState: { error },
  industries,
  isNoLabel = false,
}: {
  field: Step1Field<"industry_id">;
  fieldState: ControllerFieldState;
  industries: { id: string | number; name: string }[];
  isNoLabel?: boolean;
}) => {
  return (
    <Select
      required
      label={isNoLabel ? undefined : "主業種"}
      error={error?.message ?? ""}
      {...field}
    >
      <option value="" key="empty">
        選択してください
      </option>
      {industries.map((industry) => (
        <option value={industry.id} key={industry.id}>
          {industry.name}
        </option>
      ))}
    </Select>
  );
};

export const EmployeesField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
  inputProps,
}: {
  field: Step1Field<"number_of_employees">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
  inputProps?: InputProps;
}) => {
  return (
    <Box>
      <HStack>
        <Box>
          <Input
            {...field}
            required
            id="number_of_employees"
            label={isNoLabel ? undefined : "従業員数"}
            error={error?.message}
            {...inputProps}
          />
        </Box>
        <Box>名</Box>
      </HStack>
      <Text fontSize="xs" color="#6D787D" mt={1}>
        従業員がいない場合は0を入力してください。
      </Text>
    </Box>
  );
};

export const EstablishmentYearField = ({
  field,
  fieldState: { error },
}: {
  field: Step1Field<"establishment_year">;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Select
      required
      error={error?.message ?? ""}
      {...field}
      aria-label="設立年"
    >
      <option value="" key="empty"></option>
      {getEstablishmentYears().map((year) => (
        <option value={year} key={year}>
          {year}
        </option>
      ))}
    </Select>
  );
};

export const EstablishmentMonthField = ({
  field,
  fieldState: { error },
}: {
  field: Step1Field<"establishment_month">;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Select
      required
      error={error?.message ?? ""}
      {...field}
      aria-label="設立月"
    >
      <option value="" key="empty"></option>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
        <option value={month} key={month}>
          {month}
        </option>
      ))}
    </Select>
  );
};

export const CapitalField = ({
  field,
  fieldState: { error },
  inputProps,
}: {
  field: Step1Field<"capital_ten_thousand_yen">;
  fieldState: ControllerFieldState;
  inputProps?: InputProps;
}) => {
  return (
    <Box>
      <HStack>
        <Box>
          <Input
            {...field}
            required
            error={error?.message}
            id="capital_ten_thousand_yen"
            {...inputProps}
          />
        </Box>
        <Box minW="fit-content">万円</Box>
      </HStack>
      <Text fontSize="xs" color="#6D787D" mt={1}>
        資本金を持たない場合は、「0」を入力してください。
      </Text>
    </Box>
  );
};

export const PresidentNameField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"president_name">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      required
      label={isNoLabel ? undefined : "代表者氏名"}
      placeholder="例）建築 太郎"
      error={error?.message}
    />
  );
};

export const WebsiteField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"website_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "Webサイト"}
      error={error?.message}
      placeholder="例）https://aloop.jp"
    />
  );
};

export const FacebookUrlField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"facebook_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "Facebook"}
      error={error?.message}
      placeholder="例）https://www.facebook.com"
    />
  );
};

export const InstagramUrlField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"instagram_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "Instagram"}
      error={error?.message}
      placeholder="例）https://www.instagram.com"
    />
  );
};

export const NoteUrlField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"note_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "note"}
      error={error?.message}
      placeholder="例）https://note.com"
    />
  );
};

export const PinterestUrlField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"pinterest_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "Pinterest"}
      error={error?.message}
      placeholder="例）https://www.pinterest.jp"
    />
  );
};

export const ThreadsUrlField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"threads_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "Threads"}
      error={error?.message}
      placeholder="例）https://www.threads.net"
    />
  );
};

export const TiktokUrlField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"tiktok_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "TikTok"}
      error={error?.message}
      placeholder="例）https://www.tiktok.com"
    />
  );
};

export const XUrlField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"x_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "X（旧Twitter）"}
      error={error?.message}
      placeholder="例）https://twitter.com"
    />
  );
};

export const YoutubeUrlField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step1Field<"youtube_url">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Input
      {...field}
      label={isNoLabel ? undefined : "YouTube"}
      error={error?.message}
      placeholder="例）https://www.youtube.com"
    />
  );
};

export const UseCaseField = ({
  field: { ref, ...field },
  fieldState: { error },
}: {
  field: Step2Field<"use_case">;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Box>
      <RadioGroup {...field} isDisabled={field.disabled}>
        <Stack>
          {useCaseOptions.map(({ label, value }) => (
            <Radio
              value={value}
              key={value}
              ref={ref}
              isInvalid={!!error}
              w="fit-content"
            >
              {label}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
      {error && <InputError>{error?.message}</InputError>}
    </Box>
  );
};

export const IntroductionField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step2Field<"introduction">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Textarea
      {...field}
      label={isNoLabel ? undefined : "事業者紹介"}
      placeholder={`例）建築意匠設計
　・住宅、商業施設、オフィスビル、公共施設などの建築設計
　・クライアントのニーズに合わせたデザイン提案・コンセプト設計
　・環境・景観に配慮したサステナブルな建築デザイン
　
　インテリアデザイン
　・店舗・オフィス・ホテル・住宅の内装デザイン
　・空間コンセプトの企画立案および設計施工監理
　・照明・家具・マテリアルの選定・コーディネート`}
      required
      rows={10}
      error={error?.message}
      message={"1000文字まで"}
    />
  );
};

export const MainClientField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step2Field<"main_client">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Textarea
      {...field}
      label={isNoLabel ? undefined : "主な取引先"}
      placeholder={`例）・エーループ設計事務所
　　・エーループ構造設計事務所
　　・エーループ建設会社`}
      error={error?.message}
      rows={5}
      message="500文字まで"
      _placeholder={{ textSpacingTrim: "space-all" }}
    />
  );
};

export const ContractorInfoSupportedBusinessServiceNoteField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step2Field<"contractor_info_attributes.supported_business_service_note">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Textarea
      {...field}
      label={isNoLabel ? undefined : "対応できる業務内容"}
      placeholder={`例）1.設計・デザイン提案
　　2. 基本設計・実施設計
　　・建築デザインのコンセプト立案・プランニング
　　・建築図面（平面図・立面図・断面図・詳細図）の作成
　　・構造設計・設備設計との連携による最適な設計提案
　　3.現場監理・施工業者との調整業務
　　・設計通りの仕上がりを確保する品質チェック
　　・納期管理・コストコントロール`}
      required
      rows={8}
      message="1000文字まで"
      error={error?.message}
    />
  );
};

export const ContractorInfoArchitecturalDesignWorksProjectNameField = ({
  field,
  fieldState: { error },
}: {
  field: Step2Field<`contractor_info_attributes.architectural_design_works_attributes.${number}.project_name`>;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Input
      {...field}
      label="プロジェクト名"
      required
      placeholder="例）エーループ住宅"
      error={error?.message}
      message={
        <>
          正式なプロジェクト名ではなく、イメージが伝わるような仮の名称でも可能です。
          <br />
          30文字まで
        </>
      }
    />
  );
};

export const ContractorInfoArchitecturalDesignWorksBuildingTypeField = ({
  field,
  fieldState: { error },
  buildingTypes,
}: {
  field: Step2Field<`contractor_info_attributes.architectural_design_works_attributes.${number}.building_type_id`>;
  fieldState: ControllerFieldState;
  buildingTypes: Options;
}) => {
  return (
    <Select {...field} label="建物種別" required error={error?.message}>
      <option value="" key="empty">
        選択してください
      </option>
      {buildingTypes.map((buildingType) => (
        <option value={buildingType.id} key={buildingType.id}>
          {buildingType.name}
        </option>
      ))}
    </Select>
  );
};

export const ContractorInfoArchitecturalDesignWorksProjectDetailField = ({
  field,
  fieldState: { error },
}: {
  field: Step2Field<`contractor_info_attributes.architectural_design_works_attributes.${number}.project_detail`>;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Textarea
      {...field}
      label="業務内容"
      placeholder={`例）1.設計・デザイン提案
　　2. 基本設計・実施設計
　　・建築デザインのコンセプト立案・プランニング
　　・建築図面（平面図・立面図・断面図・詳細図）の作成
　　・構造設計・設備設計との連携による最適な設計提案
　　3.現場監理・施工業者との調整業務
　　・設計通りの仕上がりを確保する品質チェック
　　・納期管理・コストコントロール`}
      required
      rows={8}
      error={error?.message}
      message="1000文字まで"
    />
  );
};

export const ContractorInfoArchitecturalDesignWorksPrefectureField = ({
  field,
  fieldState: { error },
  prefectures,
}: {
  field: Step2Field<`contractor_info_attributes.architectural_design_works_attributes.${number}.prefecture_id`>;
  fieldState: ControllerFieldState;
  prefectures: Options;
}) => {
  return (
    <Select
      {...field}
      label="所在地"
      error={error?.message}
      w={{ base: "full", md: "50%" }}
    >
      <option value="" key="empty">
        選択してください
      </option>
      {prefectures.map((prefecture) => (
        <option value={prefecture.id} key={prefecture.id}>
          {prefecture.name}
        </option>
      ))}
    </Select>
  );
};

export const ContractorInfoArchitecturalDesignWorksAboveGroundFloorsField = ({
  field,
  fieldState: { error },
}: {
  field: Step2Field<`contractor_info_attributes.architectural_design_works_attributes.${number}.above_ground_floors`>;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Input
      {...field}
      label="地上"
      placeholder="例）5"
      error={error?.message}
      w={{ base: "full", md: "50%" }}
      rightElement={() => (
        <HStack minW="fit-content" ml={2}>
          <Box>階建</Box>
        </HStack>
      )}
    />
  );
};

export const ContractorInfoArchitecturalDesignWorksUndergroundFloorsField = ({
  field,
  fieldState: { error },
}: {
  field: Step2Field<`contractor_info_attributes.architectural_design_works_attributes.${number}.underground_floors`>;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Input
      {...field}
      label="地下"
      placeholder="例）2"
      error={error?.message}
      w={{ base: "full", md: "50%" }}
      rightElement={() => (
        <HStack minW="fit-content" ml={2}>
          <Box>階建</Box>
        </HStack>
      )}
    />
  );
};

export const ContractorInfoArchitecturalDesignWorksTotalFloorAreaField = ({
  field,
  fieldState: { error },
}: {
  field: Step2Field<`contractor_info_attributes.architectural_design_works_attributes.${number}.total_floor_area`>;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Input
      {...field}
      label="延床面積"
      error={error?.message}
      placeholder="例）150"
      w={{ base: "full", md: "50%" }}
      rightElement={() => (
        <HStack minW="fit-content" ml={2}>
          <Box>㎡</Box>
        </HStack>
      )}
    />
  );
};

export const ContractorInfoArchitecturalDesignWorksCompletionYearField = ({
  field,
  fieldState: { error },
}: {
  field: Step2Field<`contractor_info_attributes.architectural_design_works_attributes.${number}.completion_year`>;
  fieldState: ControllerFieldState;
}) => {
  return (
    <Input
      {...field}
      label="竣工年"
      error={error?.message}
      w={{ base: "full", md: "50%" }}
      placeholder="例）2021"
      rightElement={() => (
        <HStack minW="fit-content" ml={2}>
          <Box>年</Box>
        </HStack>
      )}
    />
  );
};

export const ContractorInfoArchitecturalDesignWorksImagesField = ({
  files,
  addFiles,
  removeFile,
  isDisabled = false,
}: {
  files: (FileType & { is_image?: boolean })[];
  addFiles: (files: File[]) => void;
  removeFile: (file: FileType) => void;
  isDisabled?: boolean;
}) => {
  const dropzoneInputRef = React.useRef<HTMLInputElement>(null);

  const handlePlaceholderClick = () => {
    if (dropzoneInputRef.current) {
      dropzoneInputRef.current.click();
    }
  };

  return (
    <Box>
      {!isDisabled && (
        <Dropzone
          accept={{
            "image/png": [],
            "image/jpeg": [],
          }}
          croppable
          onDrop={(files) => addFiles(files)}
          aspectRatio={3 / 2}
          inputRef={dropzoneInputRef}
          message={
            <Box mt={1.5} as="span">
              外観や内容などの建物写真をアップロードしてください。
              <br />
              PNGもしくはJPGファイルで、5MB未満のサイズでアップロードしてください。
              <br />
              最大で3つまでアップロード可能です。
            </Box>
          }
        />
      )}
      <Box mt={4}>
        <UploadFiles
          uploadFiles={files}
          removeFile={isDisabled ? undefined : removeFile}
          showPlaceholders
          onPlaceholderClick={isDisabled ? undefined : handlePlaceholderClick}
        />
      </Box>
    </Box>
  );
};

export const ClientInfoDesiredBusinessServiceNoteField = ({
  field,
  fieldState: { error },
  isNoLabel = false,
}: {
  field: Step2Field<"client_info_attributes.desired_business_service_note">;
  fieldState: ControllerFieldState;
  isNoLabel?: boolean;
}) => {
  return (
    <Textarea
      {...field}
      label={isNoLabel ? undefined : "発注したい業務補足"}
      placeholder={`例）建築意匠設計業務
　　・マスタープラン作成（敷地計画・建築ボリューム検討）
　　・外観デザイン、ファサードデザインの提案
　　・建築図面（基本設計図・実施設計図）の作成および調整
　　・建材・仕上げ材の選定および仕様提案
　　施工監理・デザイン監修
　　・設計意図の実現に向けた施工監理・品質管理業務
　　・施工業者・専門業者との調整・ディレクション
　　・竣工検査・引渡し時の品質確認および改善提案`}
      required
      message="1000文字まで"
      rows={10}
      error={error?.message}
    />
  );
};
